/**
 * Namespaced sessionStorage wrapper. Per-tab, ephemeral.
 *
 * Use for multi-step form / wizard state that should NOT survive a tab
 * close but SHOULD survive an in-tab navigation. Tokens are FORBIDDEN here
 * — same runtime guard as localStorage.
 *
 * Namespace:
 *   - flow : multi-step form / wizard state, keyed by flow id
 */

const NS = {
  flow: 'glasspro:flow:',
} as const

const FORBIDDEN_KEY_PATTERN = /token|password|secret|credential|api[_-]?key|jwt|bearer|auth/i

function safeSet(key: string, value: string): void {
  if (FORBIDDEN_KEY_PATTERN.test(key)) {
    throw new Error(
      `[session] Refusing to store credential-like key "${key}". ` +
        'See docs/adr/0009-storage-architecture-and-token-strategy.md',
    )
  }
  try {
    sessionStorage.setItem(key, value)
  } catch (e) {
    if (import.meta.env.DEV) throw e
    console.warn('[session] setItem failed:', key, e)
  }
}

function safeJsonParse<T>(raw: string | null): T | null {
  if (raw === null) return null
  try {
    return JSON.parse(raw) as T
  } catch {
    return null
  }
}

export const sessionStore = {
  flow: {
    set: (id: string, value: unknown) => safeSet(`${NS.flow}${id}`, JSON.stringify(value)),
    get: <T>(id: string): T | null => safeJsonParse<T>(sessionStorage.getItem(`${NS.flow}${id}`)),
    remove: (id: string) => sessionStorage.removeItem(`${NS.flow}${id}`),
  },

  /**
   * Wholesale clear of sessionStorage. Used by logout.
   *
   * NOTE: this WILL clear infrastructure keys (chunk-reload guard, lazy
   * retry, error-boundary URL guard). That is intentional during logout
   * — the next page load is a fresh state from /login anyway, and any
   * stale chunk-reload guards from the previous session are no longer
   * relevant.
   */
  clear: () => sessionStorage.clear(),
} as const
